import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import type { HealthTone } from '@/lib/writer/telemetryTypes';

interface StatCardProps {
  label: string;
  value: React.ReactNode;
  hint?: React.ReactNode;
  tone?: HealthTone;
  className?: string;
}

const toneRing: Record<HealthTone, string> = {
  ok: 'border-border',
  warn: 'border-amber-500/60',
  bad: 'border-destructive/60',
};

const toneText: Record<HealthTone, string> = {
  ok: 'text-foreground',
  warn: 'text-amber-600 dark:text-amber-400',
  bad: 'text-destructive',
};

export const StatCard: React.FC<StatCardProps> = ({ label, value, hint, tone = 'ok', className }) => (
  <Card className={cn(toneRing[tone], className)}>
    <CardContent className="pt-5 pb-4">
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={cn('text-2xl font-semibold mt-1 tabular-nums', toneText[tone])}>{value}</div>
      {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
    </CardContent>
  </Card>
);

export const Delta: React.FC<{ current: number; previous: number; invert?: boolean; suffix?: string }> = ({
  current,
  previous,
  invert = false,
  suffix = '',
}) => {
  if (!Number.isFinite(previous) || previous === 0) {
    return <span className="text-muted-foreground">no prior data</span>;
  }
  const pct = ((current - previous) / Math.abs(previous)) * 100;
  const good = invert ? pct <= 0 : pct >= 0;
  return (
    <span className={good ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'}>
      {pct >= 0 ? '+' : ''}
      {pct.toFixed(0)}%{suffix} vs previous period
    </span>
  );
};

export default StatCard;
